import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ChangeDetectionStrategy,
  AfterViewInit,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { NotificationsService } from '@services/notifications.service';
import { TranslationService } from '@services/translation.service';
@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TableComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  // @Input() selectableRows = false;
  @Input() draggable: boolean = false; //can adjust the columns width or the rows height
  @Input() minWidth = '100%';
  @Input() clickableRows = false;
  @Input() searchByOptions = [];
  @Input() searchBy = '';
  @Input() maxHeight = '69vh'; //fixed height is needed for both sticky headers and for virtual scroll
  @Input() filterInput = '';
  @Input() hasBorders = false;
  @Input() hasAlternatingRows = false;
  @Input() createText = '';
  @Input() isLoading = false;
  @Input() isProcessing = false;
  @Input() cellInputsDisabled = false;
  @Input() actions: TableAction[] = [];
  @Input() rowMenuItems: TableRowMenuItem[] = [];
  @Input() showRowCountColumn = false;
  @Input() tableLayout = 'fixed';
  @Input() sortBy;
  @Input() rows = [];
  @Input() columns: TableColumn[] = [];
  @Input() showPaginator = false;
  @Input() pageNumber = 0;
  @Input() perPage = 10;
  @Input() perPageOptions = [{ count: 10 }, { count: 25 }, { count: 50 }];
  @Input() itemCount = 0;
  @Input() showCreate = true;
  @Input() canSearch = false;
  @Input() undefinedText;
  @Input() sortAsc;
  @Input() emptyText;
  @Input() forceRowCopyType; //force all columns and rows to be treated as this type on copy
  @Input() rowMaxHeight = 'none'; // if using virutal scroll, will default to 42px
  @Input() useVirutalScroll: boolean = false;
  @Input() activatedRoute;
  @Input() canLoadMore = false; // allow to display a button to load more at the bottom of the page
  @Input() disableLoadMore = false; //disable state for load more button
  @Input() customCountLabel: string; // to be used rather than the current item count/pagination count
  /** Map of column property -> TemplateRef for type 'custom' columns. Pass templates from parent, e.g. [cellTemplates]="{ disabled: enabledCellTpl }". */
  @Input() cellTemplates: Record<string, TemplateRef<any>> = {};
  @Output() sortChange = new EventEmitter<any>();
  @Output() activePageChange = new EventEmitter<any>();
  @Output() actionClicked = new EventEmitter<any>();

  @Output() rowMenuActionClicked = new EventEmitter<{
    action: string;
    row: any;
  }>();
  @Output() createClicked = new EventEmitter<any>();
  @Output() initSearch = new EventEmitter<any>();
  @Output() setPageCount = new EventEmitter<any>();
  @Output() updateSearchBy = new EventEmitter<any>();
  @Output() clickedRow = new EventEmitter<any>();
  @Output() clickedHeader = new EventEmitter<any>();
  @Output() checkedRow = new EventEmitter<any>();
  @Output() scaffoldLinkClicked = new EventEmitter<any>();
  @Output() rowInputUpdate = new EventEmitter<any>();
  @Output() columnRowClicked = new EventEmitter<any>();
  @Output() checkedColumn = new EventEmitter<TableColumn>();
  @Output() loadMore = new EventEmitter<void>(); // output event when load more is clicked
  // public actionHeaderWidth = "0px";
  @ViewChild('table') table: ElementRef;
  @ViewChild(CdkVirtualScrollViewport, { static: false })
  public viewPort: CdkVirtualScrollViewport;
  /** Row for which the row menu is currently open. */
  public openedRowForMenu: any = null;
  /** Position for custom row menu overlay (fixed; set when opening). */
  public rowMenuOverlayTop = 0;
  public rowMenuOverlayLeft = 0;
  private lastGoodColumnWidths: string[] = [];
  public headerTop = '0px';
  public rowMaxWidth = 'inherit';
  public tableWidth = '100%';
  public calculatedTableWidthPx: string | null = null;
  public hasSetHeaders: boolean;
  public get inverseOfTranslation(): string {
    if (!this.viewPort || !this.viewPort['_renderedContentOffset']) {
      return '-1px';
    }
    let offset = this.viewPort['_renderedContentOffset'];
    return `-${offset + 3}px`;
  }

  private startHeight: number;
  private startY: number;
  private startX: number;
  private startWidth: number;
  private currentTh: HTMLElement | null = null;
  private currentRow: HTMLElement | null = null;
  public isResizing: boolean = false;
  private isViewInitialized: boolean;
  private linkCalculationCache = new Map<
    string,
    { visibleLinks: any[]; hiddenCount: number }
  >();
  private resizeObserver: ResizeObserver;
  private hasCalculatedOnce: boolean = false;
  private lastTableWidth: number = 0;
  private resizeTimeout: any;
  private isCalculating: boolean = false;

  constructor(
    private translations: TranslationService,
    private cd: ChangeDetectorRef,
    private notification: NotificationsService
  ) {}
  ngOnInit(): void {
    // Ensure searchByOptions is always an array
    if (!Array.isArray(this.searchByOptions)) {
      this.searchByOptions = [];
    }

    // Ensure perPageOptions is always an array
    if (!Array.isArray(this.perPageOptions)) {
      this.perPageOptions = [{ count: 10 }, { count: 25 }, { count: 50 }];
    }

    // if table tableout is not fixed, table headers can be considered set
    if (this.tableLayout !== 'fixed') {
      this.hasSetHeaders = true;
    }
    // must enforce expected rowMaxHeight if using a virtual viewport to match expected item size
    if (this.shouldUseVirtualScroll && this.rowMaxHeight !== '44px') {
      this.rowMaxHeight = '44px';
    }
    if (this.undefinedText === undefined) {
      this.undefinedText = this.translations.getTranslatedContent(
        'undefined',
        'Undefined'
      );
    }
  }

  ngOnChanges(changes) {
    // Clear link calculation cache when rows change
    if (changes?.rows) {
      this.linkCalculationCache.clear();
    }

    // Ensure searchByOptions is always an array
    if (changes?.searchByOptions) {
      if (!Array.isArray(this.searchByOptions)) {
        this.searchByOptions = [];
      }
    }

    // Ensure perPageOptions is always an array
    if (changes?.perPageOptions) {
      if (!Array.isArray(this.perPageOptions)) {
        this.perPageOptions = [{ count: 10 }, { count: 25 }, { count: 50 }];
      }
    }

    if (
      changes.rows?.previousValue?.length === 0 &&
      changes.rows.currentValue?.length > 0
    ) {
      setTimeout(() => this.restoreLastGoodWidths(), 10);
    }

    // checks only for fixed tables to sort out the width if columns change
    if (this.tableLayout === 'fixed') {
      if (changes?.columns && changes?.columns?.firstChange) {
        // ensure table is 100% if fixed width
        this.tableWidth = '100%';
        this.calculatedTableWidthPx = null;
        this.hasSetHeaders = false;
        this.hasCalculatedOnce = false;
        this.lastTableWidth = 0;

        if (this.isViewInitialized) {
          // Clear any pending calculations
          clearTimeout(this.resizeTimeout);
          this.resizeTimeout = setTimeout(() => this.setHeaderBasedWidths(), 0);
        }
      }
      // Reset flags when rows change to force recalculation (e.g., when navigating back to page)
      else if (
        changes?.rows &&
        this.rows.length > 0 &&
        this.isViewInitialized &&
        !this.hasCalculatedOnce
      ) {
        this.hasSetHeaders = false;
        // Use minimal delay to ensure DOM is updated
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          if (!this.hasSetHeaders) {
            this.setHeaderBasedWidths();
          }
        }, 0);
      }
      // Only calculate if not already calculated and view is ready
      else if (
        !this.isLoading &&
        this.rows.length > 0 &&
        !this.hasSetHeaders &&
        !this.hasCalculatedOnce &&
        this.isViewInitialized
      ) {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => this.setHeaderBasedWidths(), 0);
      }
    }
  }

  ngAfterViewInit() {
    this.isViewInitialized = true;

    if (this.tableLayout === 'fixed' && this.table?.nativeElement) {
      // Only force calculation if not already calculated
      if (!this.hasCalculatedOnce) {
        this.forceInitialCalculation();
      }

      this.resizeObserver = new ResizeObserver(([entry]) => {
        const w = entry.contentRect.width;
        if (w > 0) {
          this.lastTableWidth = w;
          if (!this.hasCalculatedOnce) {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = setTimeout(
              () => this.setHeaderBasedWidths(),
              0
            );
          }
        }
      });
      this.resizeObserver.observe(this.table.nativeElement);
    }

    if (this.viewPort) {
      this.viewPort['_scrollStrategy'].onRenderedOffsetChanged = () =>
        (this.headerTop = `-${this.viewPort.getOffsetToRenderedContentStart()}px`);
    }
  }
  restoreLastGoodWidths() {
    if (this.lastGoodColumnWidths.length === this.columns.length) {
      const headers = this.table?.nativeElement.querySelectorAll(
        'th'
      ) as NodeListOf<HTMLElement>;
      headers.forEach((th, i) => {
        if (i < this.lastGoodColumnWidths.length) {
          th.style.width = this.lastGoodColumnWidths[i];
          th.style.minWidth = this.lastGoodColumnWidths[i];
        }
      });
      this.cd.detectChanges();
    }
  }

  private forceInitialCalculation() {
    let attempts = 0;
    const tryCalculate = () => {
      attempts++;
      const table = this.table.nativeElement;
      const container = table.parentElement as HTMLElement | null;
      const containerWidth = container?.clientWidth ?? 0;
      const tableWidth = table.offsetWidth;
      const hasWidth =
        (containerWidth > 0 || tableWidth > 0) && !this.hasCalculatedOnce;
      if (hasWidth) {
        this.setHeaderBasedWidths();
      } else if (attempts < 5 && !this.hasCalculatedOnce) {
        requestAnimationFrame(tryCalculate);
      }
    };
    requestAnimationFrame(tryCalculate);
  }

  ngOnDestroy() {
    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    // Clean up any pending timeouts
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  setHeaderBasedWidths() {
    if (this.tableLayout === 'auto' || !this.table?.nativeElement) return;

    const table = this.table.nativeElement;
    // Use container (scroll host) width so column distribution respects the space the table actually has.
    // table.offsetWidth can be 0 or wrong on init before layout; the parent has the real available width.
    const container = table.parentElement as HTMLElement | null;
    const containerWidth = container?.clientWidth ?? 0;
    const tableWidth = table.offsetWidth;
    const widthToUse = containerWidth > 0 ? containerWidth : tableWidth;
    if (widthToUse === 0) return;

    this.isCalculating = true;

    // Create a SINGLE measurement canvas (100x faster than DOM cloning)
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    ctx.font = '14px Roboto, sans-serif'; // Match your table font

    const headers = table.querySelectorAll('th') as NodeListOf<HTMLElement>;
    const actionButtonCount =
      (this.actions?.length || 0) + (this.rowMenuItems?.length ? 1 : 0);
    const availableWidth = widthToUse - actionButtonCount * 48 - 20;
    const minColWidth = 80;

    let totalWidth = 0;
    const columnWidths: number[] = [];
    const headerMinWidths: number[] = [];

    headers.forEach((header, index) => {
      const column = this.columns[index];
      const headerText = this.getCleanHeaderText(header);
      const headerTextMin = Math.ceil(ctx.measureText(headerText).width) + 40;
      headerMinWidths[index] = Math.max(headerTextMin, minColWidth);

      if (column?.width) {
        const width = parseInt(column.width, 10) || minColWidth;
        columnWidths[index] = width;
        totalWidth += width;
        return;
      }

      // Actions column
      if (header.classList.contains('app-table-header-actions')) {
        const width = Math.max(1, actionButtonCount) * 48 + 24;
        columnWidths[index] = width;
        totalWidth += width;
        return;
      }

      // Natural width from header + content sample (used as weight for ratio)
      const contentWidths = this.getContentWidths(canvas, ctx, index, 5);
      const contentMax = contentWidths.length
        ? Math.max(...contentWidths)
        : minColWidth;
      const naturalWidth = Math.max(headerTextMin, contentMax, minColWidth);
      columnWidths[index] = naturalWidth;
      totalWidth += naturalWidth;
    });

    // SECOND PASS: Fit to viewport – scale column widths so they use the full available width.
    if (totalWidth > 0) {
      const ratio = availableWidth / totalWidth;
      columnWidths.forEach((width, index) => {
        const scaled = Math.floor(width * ratio);
        const minForHeader = headerMinWidths[index] ?? minColWidth;
        columnWidths[index] = Math.max(scaled, minForHeader);
      });
    }

    // APPLY WIDTHS to headers and body cells so column widths are respected (table width 100% can override th-only)
    const totalTableWidth = columnWidths.reduce((a, b) => a + b, 0);
    headers.forEach((header, index) => {
      const w = `${columnWidths[index]}px`;
      header.style.width = w;
      header.style.minWidth = w;
    });
    const rows = table.querySelectorAll('tbody tr');
    rows.forEach(row => {
      const cells = (row as HTMLTableRowElement).cells;
      columnWidths.forEach((w, index) => {
        const cell = cells[index] as HTMLElement | undefined;
        if (cell) {
          const wpx = `${w}px`;
          cell.style.width = wpx;
          cell.style.minWidth = wpx;
        }
      });
    });

    this.calculatedTableWidthPx = `${totalTableWidth}px`;
    this.hasSetHeaders = true;
    this.lastTableWidth = widthToUse;
    this.isCalculating = false;
    this.hasCalculatedOnce = true;

    (this.table.nativeElement as HTMLElement).classList.add(
      'headers-calculated'
    );

    // Immediate change detection to prevent glitchy appearance
    this.linkCalculationCache.clear();
    this.cd.detectChanges();
    this.lastGoodColumnWidths = columnWidths.map(w => `${w}px`);
  }

  // FAST text measurement using canvas
  private getCleanHeaderText(header: HTMLElement): string {
    return header.textContent?.replace(/[\s\n\t]+/g, ' ').trim() || '';
  }

  private getContentWidths(
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    colIndex: number,
    sampleCount: number = 5
  ): number[] {
    const table = this.table.nativeElement;
    const allRows = table.querySelectorAll('tr');
    const widths: number[] = [];

    // Simple loop with explicit typing - NO Array.from needed
    for (let i = 1; i <= sampleCount && i < allRows.length; i++) {
      const row = allRows[i] as HTMLTableRowElement;
      if (!row || !row.cells[colIndex]) continue;

      const cell = row.cells[colIndex] as HTMLElement;
      const column = this.columns[colIndex];

      let text = '';

      if (column?.type === 'locallinks') {
        const links = cell.querySelectorAll('.locallink-item');
        for (let j = 0; j < links.length; j++) {
          const link = links[j] as HTMLElement;
          if (link.textContent) text += link.textContent + ' ';
        }
      } else {
        text = cell.textContent?.replace(/[\s\n\t]+/g, ' ').trim() || '';
      }

      if (text) {
        widths.push(ctx.measureText(text).width + 20);
      }
    }

    return widths.length ? widths : [80];
  }

  getHeaderTextWidth(element: HTMLElement): number {
    // Create a clone without sort arrows to measure just the text content
    const clone = element.cloneNode(true) as HTMLElement;

    // Remove sort arrow elements from the clone
    const sortArrows = clone.querySelectorAll('i.fas');
    sortArrows.forEach(arrow => arrow.remove());

    // Remove any span elements that might contain sort arrows
    const spans = clone.querySelectorAll('span[style*="padding-left"]');
    spans.forEach(span => span.remove());

    clone.style.visibility = 'hidden';
    clone.style.position = 'absolute';
    clone.style.width = 'auto';
    clone.style.whiteSpace = 'nowrap';
    document.body.appendChild(clone);
    const width = clone.offsetWidth;
    document.body.removeChild(clone);

    return width;
  }

  isContentHeavyColumn(
    column: TableColumn,
    headerElement: HTMLElement
  ): boolean {
    if (!column) return false;

    // check if header text is long
    const headerText = headerElement.textContent?.trim() || '';
    if (headerText.length > 15) {
      return true;
    }

    return false;
  }

  private syncCalculatedTableWidthAfterResize(): void {
    if (!this.lastGoodColumnWidths.length) return;
    const total = this.lastGoodColumnWidths.reduce(
      (sum, w) => sum + (parseInt(String(w), 10) || 0),
      0
    );
    if (total > 0) {
      this.calculatedTableWidthPx = `${total}px`;
    }
  }

  private applyColumnWidth(columnIndex: number, widthPx: string): void {
    if (!this.table?.nativeElement) return;
    const table = this.table.nativeElement;
    const headers = table.querySelectorAll('th') as NodeListOf<HTMLElement>;
    const th = headers[columnIndex];
    if (th) {
      th.style.width = widthPx;
      th.style.minWidth = widthPx;
    }
    const rows = table.querySelectorAll('tbody tr');
    rows.forEach(row => {
      const cell = (row as HTMLTableRowElement).cells[columnIndex] as
        | HTMLElement
        | undefined;
      if (cell) {
        cell.style.width = widthPx;
        cell.style.minWidth = widthPx;
      }
    });
  }

  onColumnMouseDown(event: MouseEvent) {
    this.isResizing = true;

    event.stopPropagation();
    event.stopImmediatePropagation();
    event.preventDefault();
    this.startX = event.pageX;
    this.currentTh = (event.target as HTMLElement).parentElement;
    this.startWidth = this.currentTh?.offsetWidth || 0;

    document.addEventListener('mousemove', this.onColumnMouseMove);
    document.addEventListener('mouseup', this.onColumnMouseUp);
  }

  onColumnMouseMove = (event: MouseEvent) => {
    if (!this.currentTh || !this.table?.nativeElement) return;
    const deltaX = event.pageX - this.startX;
    const newWidth = this.startWidth + deltaX;
    const widthPx = newWidth > 100 ? `${newWidth}px` : '100px';
    const headers = this.table.nativeElement.querySelectorAll(
      'th'
    ) as NodeListOf<HTMLElement>;
    const columnIndex = Array.from(headers).indexOf(this.currentTh);
    if (columnIndex >= 0) {
      this.applyColumnWidth(columnIndex, widthPx);
    }
  };

  onColumnMouseUp = () => {
    if (this.currentTh && this.table?.nativeElement) {
      const widthPx = this.currentTh.style.width;
      const headers = this.table.nativeElement.querySelectorAll(
        'th'
      ) as NodeListOf<HTMLElement>;
      const index = Array.from(headers).indexOf(this.currentTh);
      if (widthPx && index >= 0 && index < this.lastGoodColumnWidths.length) {
        this.lastGoodColumnWidths[index] = widthPx;
      }
      this.syncCalculatedTableWidthAfterResize();
    }
    document.removeEventListener('mousemove', this.onColumnMouseMove);
    document.removeEventListener('mouseup', this.onColumnMouseUp);
    this.currentTh = null;
    setTimeout(() => (this.isResizing = false), 10);
    this.linkCalculationCache.clear();
    this.cd.detectChanges();
  };

  onRowMouseDown(event: MouseEvent) {
    // avoid other actions when resizing a row
    this.isResizing = true;
    event.stopPropagation();
    event.stopImmediatePropagation();
    event.preventDefault();
    this.startY = event.pageY;
    this.currentRow = (event.target as HTMLElement).closest('tr');
    this.startHeight = this.currentRow?.offsetHeight || 0;

    document.addEventListener('mousemove', this.onRowMouseMove);
    document.addEventListener('mouseup', this.onRowMouseUp);
  }

  trackByAction(index: number, action: TableAction) {
    return action.type;
  }

  trackByColumn(index: number, column: TableColumn) {
    return column?.property;
  }

  onRowMouseMove = (event: MouseEvent) => {
    if (this.currentRow) {
      const newHeight = this.startHeight + (event.pageY - this.startY);
      this.currentRow.style.height = newHeight > 50 ? `${newHeight}px` : '50';
      const finalHeight = newHeight > 50 ? `${newHeight}px` : '50';
      this.currentRow.style.height = finalHeight;
      // Update each child .app-table-content-wrapper max-height
      const contentWrappers = this.currentRow.querySelectorAll(
        '.app-table-content-wrapper'
      );
      this.currentRow.style.height = finalHeight;
      contentWrappers.forEach((wrapper: any) => {
        (wrapper as HTMLElement).style.maxHeight = finalHeight;
      });
    }
  };

  onRowMouseUp = () => {
    this.isResizing = false;
    setTimeout(() => (this.isResizing = false), 10);

    document.removeEventListener('mousemove', this.onRowMouseMove);
    document.removeEventListener('mouseup', this.onRowMouseUp);
    this.currentRow = null;
  };

  onRowColumnMouseDown(event: MouseEvent, column) {
    this.isResizing = true;
    event.stopPropagation();
    event.stopImmediatePropagation();
    event.preventDefault();
    this.startX = event.pageX;
    this.startY = event.pageY;

    // Determine if resizing a row or column
    this.currentRow = (event.target as HTMLElement).closest('tr');
    const tableElement = this.table.nativeElement;
    const headers = tableElement.querySelectorAll('th');
    this.currentTh = Array.from(headers).find(th => {
      return (
        (th as HTMLElement).innerText.trim().toLowerCase() ===
        column?.text?.toLowerCase()
      );
    }) as HTMLElement;

    if (this.currentRow) {
      this.startHeight = this.currentRow.offsetHeight;
    }

    if (this.currentTh) {
      this.startWidth = this.currentTh.offsetWidth;
    }

    document.addEventListener('mousemove', this.onRowColumnMouseMove);
    document.addEventListener('mouseup', this.onRowColumnMouseUp);
  }

  onRowColumnMouseMove = (event: MouseEvent) => {
    if (!this.currentTh || !this.table?.nativeElement) return;
    const deltaX = event.pageX - this.startX;
    const newWidth = this.startWidth + deltaX;
    const widthPx = newWidth > 100 ? `${newWidth}px` : '100px';
    const headers = this.table.nativeElement.querySelectorAll(
      'th'
    ) as NodeListOf<HTMLElement>;
    const columnIndex = Array.from(headers).indexOf(this.currentTh);
    if (columnIndex >= 0) {
      this.applyColumnWidth(columnIndex, widthPx);
    }
  };

  onRowColumnMouseUp = () => {
    if (this.currentTh && this.table?.nativeElement) {
      const widthPx = this.currentTh.style.width;
      const headers = this.table.nativeElement.querySelectorAll(
        'th'
      ) as NodeListOf<HTMLElement>;
      const index = Array.from(headers).indexOf(this.currentTh);
      if (widthPx && index >= 0 && index < this.lastGoodColumnWidths.length) {
        this.lastGoodColumnWidths[index] = widthPx;
      }
      this.syncCalculatedTableWidthAfterResize();
    }
    document.removeEventListener('mousemove', this.onRowColumnMouseMove);
    document.removeEventListener('mouseup', this.onRowColumnMouseUp);
    this.currentTh = null;
    this.isResizing = false;

    // Trigger change detection to update overflow states
    this.cd.detectChanges();
  };

  handleCheckedRow(row, column) {
    if (!row || row[column.property] == null) {
      return;
    }
    // Check if the value is a FormControl
    if (row[column.property] instanceof FormControl) {
      // Don't allow changes if the control is disabled
      if (row[column.property].disabled) {
        return;
      }
      // If it's a FormControl, toggle its value
      const currentValue = row[column.property].value;
      row[column.property].setValue(!currentValue);
    } else if (
      typeof row[column.property] === 'object' &&
      row[column.property] !== null &&
      'value' in row[column.property]
    ) {
      row[column.property].value = !row[column.property].value;
    } else {
      // Update the row value directly (for boolean values)
      row[column.property] = !row[column.property];
    }
    this.checkedRow.emit({ row, column });
    // Trigger change detection since we're using OnPush strategy
    this.cd.detectChanges();
  }

  handleClickedRow($event, row) {
    if (this.isResizing) return;
    // avoid when clicking on controls that handle their own click (toggle, checkbox, input, etc.)
    if (
      !$event?.target ||
      $event?.target.closest('app-input-toggle') ||
      $event?.target.closest('app-input-toggle-small') ||
      $event?.target.closest('textarea') ||
      $event?.target.closest('input') ||
      $event?.target.closest('p-checkbox') ||
      $event?.target.closest('.p-checkbox')
    )
      return;
    if (!this.clickableRows) return;
    this.clickedRow.emit({ $event, row });
  }

  handleUpdateSearchBy($event) {
    this.updateSearchBy.emit($event);
  }

  handleActivePageChange($event) {
    this.activePageChange.emit($event);
  }

  handleActionClicked($event, action, row) {
    if ($event) {
      $event?.stopPropagation();
      $event?.preventDefault();
    }
    this.actionClicked.emit({ action, row });
  }

  getRowMenuOverlayItems(): {
    label: string;
    disabled: boolean;
    action: string;
    disabledText?: string;
  }[] {
    const row = this.openedRowForMenu;
    if (!row || !this.rowMenuItems?.length) return [];
    return this.rowMenuItems.map(item => {
      const disabled =
        typeof item.disabled === 'function'
          ? item.disabled(row)
          : !!item.disabled;
      const label = item.getText
        ? item.getText(row)
        : this.translations.getTranslatedContent(
            item.translationKey || '',
            item.translationKey || ''
          );
      return {
        label,
        disabled,
        action: item.action,
        disabledText:
          disabled && item.disabledText ? item.disabledText : undefined,
      };
    });
  }

  openRowMenu($event: Event, row: any) {
    $event?.stopPropagation();
    $event?.preventDefault();
    this.openedRowForMenu = row;
    const target = $event?.target as HTMLElement;
    if (target) {
      const rect = target.getBoundingClientRect();
      this.rowMenuOverlayTop = rect.bottom + 2;
      this.rowMenuOverlayLeft = rect.left + rect.width / 2;
    }
    this.cd.detectChanges();
  }

  closeRowMenu() {
    this.openedRowForMenu = null;
    this.cd.detectChanges();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.openedRowForMenu || !this.rowMenuItems?.length) return;
    const target = event.target as HTMLElement;
    if (target?.closest?.('.app-table-row-menu-overlay')) return;
    if (target?.closest?.('.app-table-row-menu-trigger')) return;
    this.closeRowMenu();
  }

  handleRowMenuAction(action: string, row: any) {
    this.closeRowMenu();
    this.rowMenuActionClicked.emit({ action, row });
    this.cd.detectChanges();
  }

  handleColumnRowClicked($event, row, property) {
    if (this.isResizing) return;
    if ($event) {
      $event?.stopPropagation();
      $event?.preventDefault();
    }
    this.columnRowClicked.emit({ row, property });
  }

  handleCreateClicked() {
    this.createClicked.emit();
  }
  handleInitSearch(clear) {
    if (clear) this.filterInput = '';
    this.initSearch.emit(this.filterInput);
  }
  handleSetPageCount($event) {
    this.setPageCount.emit($event?.value);
  }

  handleSortChange($event) {
    if (this.isResizing) return;
    if ($event.isSortable) {
      this.sortChange.emit($event);
    }
  }

  handleCheckedHeaderRow($event, column) {
    this.clickedHeader.emit({ $event, column });
  }

  handleScaffoldLink($event, row, column) {
    $event.preventDefault();
    $event.stopPropagation();
    this.scaffoldLinkClicked.emit({ $event, row, column });
  }
  handleRowInputUpdate($event, type, row, column) {
    if ($event) {
      $event?.stopPropagation();
      $event?.preventDefault();
    }

    this.rowInputUpdate.emit({ type, row, column });
  }

  getCountLabel() {
    const of = this.translations.getTranslatedContent('of', 'of');
    const items = this.translations.getTranslatedContent('items', 'Items');
    const start = this.pageNumber * this.perPage - this.perPage + 1;
    const end = Math.min(this.pageNumber * this.perPage, this.itemCount);
    return `${start} - ${end} ${of} ${this.itemCount} ${items}`;
  }

  trackByFn(index, item) {
    return item?.id || index;
  }

  get shouldUseVirtualScroll() {
    return this.useVirutalScroll && this.rows?.length >= 30;
  }

  get shouldShowLoadMore() {
    return this.canLoadMore && this.rows?.length < this.itemCount;
  }

  getVirtualHeight() {
    let maxHeightPx = this.maxHeight;
    if (typeof this.maxHeight === 'string' && this.maxHeight.includes('vh')) {
      const value = parseFloat(this.maxHeight);
      maxHeightPx = `${(window.innerHeight * value) / 100}px`;
    }
    const itemSize = 44;
    const calculatedHeight = this.rows?.length
      ? this.rows.length * itemSize
      : 0;
    const maxHeightValue = parseFloat(maxHeightPx);

    const headerHeight = 44;
    const totalContentHeight = calculatedHeight + headerHeight;

    return Math.min(totalContentHeight, maxHeightValue) + 'px';
  }

  getURL(item) {
    if (!item) return '';
    let _rel_url = '';
    if (
      this.activatedRoute &&
      this.activatedRoute.parent &&
      this.activatedRoute.parent.component
    ) {
      this.activatedRoute.parent.url.subscribe(val => {
        const parts = val.map(v => {
          return v.path;
        });
        _rel_url = '/' + parts.join('/') + '/';
        _rel_url += item?.type + '/' + item?.id;
      });
    } else {
      _rel_url = '/';
      _rel_url += item?.type + '/' + item?.id;
    }
    return _rel_url;
  }

  handleCheckedColumn(column) {
    // Update the column header checkbox value directly
    column.headerCheckboxValue = !column.headerCheckboxValue;
    this.checkedColumn.emit(column);
    // Trigger change detection since we're using OnPush strategy
    this.cd.detectChanges();
  }

  handleLoadMore() {
    this.loadMore.emit();
  }

  scrollBodyToTop(): void {
    const container = this.table?.nativeElement?.closest(
      '.app-table-container'
    ) as HTMLElement | null;
    if (container) {
      container.scrollTop = 0;
    }
    if (this.viewPort) {
      this.viewPort.scrollToIndex(0);
      const vpEl = this.viewPort.elementRef.nativeElement;
      if (vpEl.scrollTop > 0) {
        vpEl.scrollTop = 0;
      }
      this.viewPort.checkViewportSize();
    }
  }

  handleTextOverflowed(row: any, column: any, value: boolean) {
    if (row && column?.property && row[column.property]) {
      row[column.property].isTextOverflowed = value;
    }
  }

  async copyCellContent(event: MouseEvent, row, column) {
    //avoid if meant to navigate on click
    if (this.clickableRows) return;
    const validType = ['number', 'string', 'html', 'date', 'fileSize'];
    const colType = this.forceRowCopyType || column?.type;
    //no column type defaults to string
    const matchesDefaultCopyTypes =
      (column && !colType) || validType.includes(colType);
    const isCopyableTemplate =
      colType === 'template' && this.isTemplateCellEligibleForCopy(row, column);

    if (!matchesDefaultCopyTypes && !isCopyableTemplate) {
      return;
    }

    event.stopPropagation();
    let textToCopy = '';
    const content = row?.[column?.property];
    if (content === null || content === undefined) {
      return;
    } else if (typeof content === 'object') {
      textToCopy = content.text || JSON.stringify(content);
    } else {
      textToCopy = String(content);
    }

    if (!navigator.clipboard?.writeText) {
      return;
    }
    try {
      await navigator.clipboard.writeText(textToCopy);
      this.notification.popNotification(
        '',
        this.translations.getTranslatedContent('copied', 'Copied')
      );
    } catch {
      this.notification.popNotification(
        '',
        this.translations.getTranslatedContent(
          'copy_failed',
          'Could not copy to clipboard'
        ),
        5,
        { status: 'danger' }
      );
    }
  }

  private isTemplateCellEligibleForCopy(row: any, column: any): boolean {
    const cell = row?.[column?.property];
    if (cell == null || typeof cell !== 'object') {
      return true;
    }
    switch (cell.viewType) {
      case 'button-link':
        return false;
      case 'locallink':
        return !cell.link;
      default:
        return true;
    }
  }

  isOverflowing(container: HTMLElement, totalCount: number): boolean {
    if (!container || totalCount <= 1) return false;

    // For locallinks containers, we need to check if the content would overflow
    // by measuring the actual content width vs container width
    const containerWidth = container.offsetWidth;

    // Create a clone to measure the natural width of all content
    const clone = container.cloneNode(true) as HTMLElement;
    clone.style.visibility = 'hidden';
    clone.style.position = 'absolute';
    clone.style.width = 'auto';
    clone.style.flexWrap = 'nowrap';
    clone.style.overflow = 'visible';
    clone.style.display = 'flex';
    document.body.appendChild(clone);

    const naturalWidth = clone.offsetWidth;
    document.body.removeChild(clone);

    // Account for the overflow count badge space (approximately 30px)
    const availableWidth = containerWidth - 30;

    return naturalWidth > availableWidth;
  }

  /**
   * Get cached calculation or calculate and cache
   */
  private getCachedLinkCalculation(
    links: any[],
    container: HTMLElement,
    row: any,
    column: any
  ): { visibleLinks: any[]; hiddenCount: number } | undefined {
    if (!links || links.length === 0) {
      return { visibleLinks: [], hiddenCount: 0 };
    }

    // Create a unique cache key based on row, column, and links count
    const cacheKey = `${row?.id || JSON.stringify(row)}_${column?.property}_${links.length}`;

    // Check if we have a cached result
    if (this.linkCalculationCache.has(cacheKey)) {
      return this.linkCalculationCache.get(cacheKey);
    }

    // Calculate and cache the result
    const containerWidth = container?.offsetWidth || 300;
    const result = this.calculateVisibleLinks(links, containerWidth);
    this.linkCalculationCache.set(cacheKey, result);

    return result;
  }

  /**
   * Calculate which links should be visible and which should be hidden
   * Returns an object with visibleLinks and hiddenCount
   */
  calculateVisibleLinks(
    links: any[],
    containerWidth: number
  ): { visibleLinks: any[]; hiddenCount: number } {
    if (!links || links.length === 0) {
      return { visibleLinks: [], hiddenCount: 0 };
    }

    if (links.length === 1) {
      return { visibleLinks: links, hiddenCount: 0 };
    }

    // Reserve minimal space for overflow count badge (positioned absolutely, so it overlays)
    const overflowBadgeWidth = 35; // Badge min width: ~30px + small margin
    const linkMargin = 8; // margin-right between links
    const truncatedWidth = 110; // Width when a link is truncated

    // Strategy: Maximize space usage by fitting as many links as possible
    let totalWidth = 0;
    const visibleLinks: any[] = [];
    const availableWidthWithBadge = containerWidth - overflowBadgeWidth;

    for (let i = 0; i < links.length; i++) {
      const link = links[i];
      const linkWidth = this.measureLinkWidth(link.text);
      const marginWidth = i > 0 ? linkMargin : 0;
      const totalWidthWithFullLink = totalWidth + linkWidth + marginWidth;

      // If this is the last link, we don't need overflow badge space
      if (i === links.length - 1) {
        if (totalWidthWithFullLink <= containerWidth) {
          visibleLinks.push({
            ...link,
            needsTruncation: false,
          });
        } else {
          // Try truncated version of last link
          const totalWidthWithTruncated =
            totalWidth + truncatedWidth + marginWidth;
          if (totalWidthWithTruncated <= containerWidth) {
            visibleLinks.push({
              ...link,
              needsTruncation: true,
            });
          }
        }
        break;
      }

      // Try to fit this link at full width (with overflow badge space reserved)
      if (totalWidthWithFullLink <= availableWidthWithBadge) {
        visibleLinks.push({
          ...link,
          needsTruncation: false,
        });
        totalWidth = totalWidthWithFullLink;
      } else {
        // This link doesn't fit at full width
        // Try truncating it to see if we can squeeze it in
        const totalWidthWithTruncated =
          totalWidth + truncatedWidth + marginWidth;
        if (totalWidthWithTruncated <= availableWidthWithBadge + 55) {
          visibleLinks.push({
            ...link,
            needsTruncation: true,
          });
        }
        // Stop here - once we truncate or can't fit, we're done
        break;
      }
    }

    // If we couldn't fit any links, show at least the first one (truncated)
    if (visibleLinks.length === 0 && links.length > 0) {
      visibleLinks.push({
        ...links[0],
        needsTruncation: true,
      });
    }

    const hiddenCount = links.length - visibleLinks.length;
    return { visibleLinks, hiddenCount };
  }

  /**
   * Measure the width of a link text
   */
  private measureLinkWidth(text: string): number {
    const tempSpan = document.createElement('span');
    tempSpan.style.visibility = 'hidden';
    tempSpan.style.position = 'absolute';
    tempSpan.style.whiteSpace = 'nowrap';
    tempSpan.style.fontSize = '14px'; // Match the table font size
    tempSpan.style.fontFamily = 'Roboto'; // Match the table font family
    tempSpan.textContent = text;
    document.body.appendChild(tempSpan);
    const width = tempSpan.offsetWidth;
    document.body.removeChild(tempSpan);
    return width;
  }

  /**
   * Get visible links for smart display
   */
  getVisibleLinks(
    links: any[],
    container: HTMLElement,
    row: any,
    column: any
  ): any[] {
    if (!links || links.length === 0) return [];
    if (links.length === 1) return links;

    const result = this.getCachedLinkCalculation(links, container, row, column);
    return result?.visibleLinks || [];
  }

  /**
   * Get count of hidden links
   */
  getHiddenCount(
    links: any[],
    container: HTMLElement,
    row: any,
    column: any
  ): number {
    if (!links || links.length <= 1) return 0;

    const result = this.getCachedLinkCalculation(links, container, row, column);
    return result?.hiddenCount || 0;
  }

  /**
   * Get tooltip text for overflow count
   */
  getOverflowTooltip(
    links: any[],
    container: HTMLElement,
    row: any,
    column: any
  ): string {
    if (!links || links.length <= 1) return '';

    const result = this.getCachedLinkCalculation(links, container, row, column);
    if (result?.hiddenCount && result?.hiddenCount <= 0) return '';

    const hiddenLinks = links.slice(result?.visibleLinks?.length || 0);
    return hiddenLinks.map(link => link.text).join(', ');
  }
}

export interface TableAction {
  type: 'delete' | 'edit' | 'duplicate' | 'view' | 'info';
  hide?: string;
  tooltip?: string;
  permission?: string;
}

export interface TableRowMenuItem {
  action: string;
  getText?: (row: any) => string;
  translationKey?: string;
  disabled?: boolean | ((row: any) => boolean);
  disabledText?: string;
}

export interface TableColumn {
  text: string;
  property: string;
  headerTextAlign?: 'left' | 'center' | 'right';
  type:
    | 'number'
    | 'string'
    | 'html'
    | 'boolean'
    | 'date'
    | 'link'
    | 'locallink'
    | 'locallinks'
    | 'chips'
    | 'textarea'
    | 'template'
    | 'textIcon'
    | 'checkbox'
    | 'button-link'
    | 'fileSize'
    | 'input'
    | 'toggle'
    | 'custom';
  headerCheckboxValue?: boolean;
  headerCheckboxControl?: FormControl;
  defaultValueStyleGrey?: boolean;
  defaultValueStyleItalic?: boolean;
  defaultValueText?: string;
  textTransform?: string;
  width?: string;
  isSortable?: boolean;
  isSorted?: boolean;
  translationKey?: string;
  template?: TemplateRef<any>;
  customErrorMessages?: { [key: string]: string }; //to be used for type inputs to add custom error messages for validation errors found on the form control
  maxLengthEllipse?: string; // CSS value for max-width (e.g., '200px', '10rem', 'auto') - applies text truncation with ellipsis. 'auto' to fit within column width
  hasMaxLengthEllipseTooltip?: boolean; // shows the full text value as tooltip when truncated
  tooltipProperty?: string; // when set, use row[tooltipProperty] for tooltip instead of row[property] (e.g. raw HTML for tooltip while cell shows stripped text)
  /** For `checkbox` cells: row key holding tooltip text (e.g. disabled reason). */
  cellTooltipProperty?: string;
  hasMaxLengthEllipseChip?: boolean; // shows chip with count of hidden items when maxLengthEllipse is exceeded
}
